import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useInstallationsQuery } from "../../queries/data-analysis.query";
import {
  persistInstallationId,
  readStoredInstallationId,
} from "./installation-storage";
import { CenteredLoader } from "./CenteredLoader";
import { NoInstallationsInfo } from "./NoInstallationsInfo";

export {
  clearStoredInstallationIdFromStorage,
  SELECTED_INSTALLATION_STORAGE_KEY,
} from "./installation-storage";

export interface InstallationContextProviderProps {
  children: React.ReactNode;
}

export interface InstallationContextProps {
  hasInstallation: boolean;
  selectedInstallationId: number;
  setSelectedInstallationId: (v: number) => void;
}

const InstallationContext = createContext<InstallationContextProps>({
  hasInstallation: false,
  selectedInstallationId: 0,
  setSelectedInstallationId: (v) => {},
});

export function InstallationContextProvider({
  children,
}: InstallationContextProviderProps) {
  const installationsQuery = useInstallationsQuery();
  const [selectedInstallationId, setSelectedInstallationIdState] =
    useState<number>(() => readStoredInstallationId());
  const [hasInstallation, setHasInstallation] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const setSelectedInstallationId = useCallback((v: number) => {
    setSelectedInstallationIdState(v);
    persistInstallationId(v);
  }, []);

  useEffect(() => {
    if (installationsQuery.data) {
      if (installationsQuery.data.length > 0) {
        const ids = new Set(installationsQuery.data.map((d) => d.id));
        const storedId = readStoredInstallationId();
        let nextId: number;
        if (selectedInstallationId && ids.has(selectedInstallationId)) {
          nextId = selectedInstallationId;
        } else if (storedId && ids.has(storedId)) {
          nextId = storedId;
        } else {
          nextId = installationsQuery.data[0].id;
        }
        if (nextId !== selectedInstallationId) {
          setSelectedInstallationIdState(nextId);
        }
        persistInstallationId(nextId);
        setHasInstallation(true);
      } else {
        setSelectedInstallationIdState(0);
        persistInstallationId(0);
        setHasInstallation(false);
      }
      setIsInitialized(true);
    }
  }, [installationsQuery.data, selectedInstallationId]);

  if (
    (!installationsQuery.data && installationsQuery.isLoading) ||
    !isInitialized
  ) {
    return <CenteredLoader />;
  }

  if (!installationsQuery.data || installationsQuery.data.length === 0) {
    return <NoInstallationsInfo />;
  }

  return (
    <InstallationContext.Provider
      value={{
        selectedInstallationId,
        setSelectedInstallationId,
        hasInstallation,
      }}
    >
      {children}
    </InstallationContext.Provider>
  );
}

export function useInstallationSelector() {
  return useContext(InstallationContext);
}
